import { Injectable, WritableSignal, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  finalize,
  map,
  throwError,
} from 'rxjs';
import { LoadingObject, LoadingService } from '../loaders';
import {
  UserLinkViewModel,
  CreateUserLinkRequest,
  UpdateUserLinkRequest,
  UserLinkApiResponse,
  UserLinksApiListResponse,
  UserLinkErrorResponse,
  ProfessionalCompleteness,
  SocialChannelTemplate,
  ProfessionalLinkTemplate,
  ProfessionalLinkType,
  SocialLinkType,
} from './user-links.types';

type GetUserLinksSuccessResponse = UserLinksApiListResponse;
type CreateUserLinkSuccessResponse = UserLinkApiResponse;
type UpdateUserLinkSuccessResponse = UserLinkApiResponse;
type DeleteUserLinkSuccessResponse = { message?: string; link_id?: number };

@Injectable({
  providedIn: 'root',
})
export class UserLinksService {
  private readonly http = inject(HttpClient);
  private readonly loadingService = inject(LoadingService);

  private readonly _userLinks = new BehaviorSubject<UserLinkViewModel[]>([]);
  private readonly _loadingObject = signal<LoadingObject>({});
  private readonly _error = signal<string | null>(null);

  /**
   * Predefined social channel templates for UI use.
   */
  readonly socialChannelTemplates: SocialChannelTemplate[] = [
    { type: 'linkedin', label: 'LinkedIn', icon: 'fab fa-linkedin', placeholder: 'https://linkedin.com/in/...' },
    { type: 'instagram', label: 'Instagram', icon: 'fab fa-instagram', placeholder: 'https://instagram.com/...' },
    { type: 'twitter', label: 'Twitter / X', icon: 'fab fa-twitter', placeholder: 'https://x.com/...' },
    { type: 'youtube', label: 'YouTube', icon: 'fab fa-youtube', placeholder: 'https://youtube.com/...' },
    { type: 'tiktok', label: 'TikTok', icon: 'fa-brands fa-tiktok', placeholder: 'https://tiktok.com/@...' },
    { type: 'slideshare', label: 'SlideShare', icon: 'fas fa-file-powerpoint', placeholder: 'https://slideshare.net/...' },
    { type: 'twitch', label: 'Twitch', icon: 'fab fa-twitch', placeholder: 'https://twitch.tv/...' },
    { type: 'reddit', label: 'Reddit', icon: 'fab fa-reddit', placeholder: 'https://reddit.com/u/...' },
    { type: 'snapchat', label: 'Snapchat', icon: 'fab fa-snapchat', placeholder: 'https://snapchat.com/add/...' },
    // { type: 'skype', label: 'Skype', icon: 'fab fa-skype', placeholder: 'skype:username?call' },
    { type: 'whatsapp', label: 'WhatsApp', icon: 'fab fa-whatsapp', placeholder: '+1 (555) 000-0000' },
    { type: 'wechat', label: 'WeChat', icon: 'fa-brands fa-weixin', placeholder: 'wechat_username' },
  ];

  /**
   * Predefined professional link templates for UI use.
   */
  professionalLinkTemplates = signal<ProfessionalLinkTemplate[]>([
    {
      type: 'linkedin',
      labelText: 'LinkedIn',
      icon: 'fa-brands fa-linkedin',
      descriptionTranslationKey: 'profile.my_linkedin_profile',
      descriptionTranslationScope: 'profile',
      placeholder: 'https://linkedin.com/in/...',
    },
    {
      type: 'organisation',
      labelTranslationKey: 'common.organisation',
      icon: 'fa-solid fa-building',
      descriptionTranslationKey: 'profile.organisation',
      descriptionTranslationScope: 'profile',
      placeholder: 'https://culture-institute.org/staff/me',
    },
    {
      type: 'project',
      labelTranslationKey: 'profile.project',
      labelTranslationScope: 'profile',
      icon: 'fa-solid fa-project-diagram',
      descriptionTranslationKey: 'profile.project_help',
      descriptionTranslationScope: 'profile',
      placeholder: 'https://example.org/project',
    },
    {
      type: 'network',
      labelTranslationKey: 'profile.network',
      labelTranslationScope: 'profile',
      icon: 'fa-solid fa-network-wired',
      descriptionTranslationKey: 'profile.network_help',
      descriptionTranslationScope: 'profile',
      placeholder: 'https://example.org/network',
    },
    {
      type: 'publication',
      labelTranslationKey: 'profile.publication',
      labelTranslationScope: 'profile',
      icon: 'fa-solid fa-newspaper',
      descriptionTranslationKey: 'profile.publication_help',
      descriptionTranslationScope: 'profile',
      placeholder: 'https://example.org/publication',
    },
  ]);

  /**
   * Public observables for consuming components.
   */
  readonly userLinks$: Observable<UserLinkViewModel[]> = this._userLinks.asObservable();

  readonly socialLinks$ = this.userLinks$.pipe(
    map((links) => links.filter((link) => link.category === 'social')),
  );

  readonly professionalLinks$ = this.userLinks$.pipe(
    map((links) => links.filter((link) => (link.category === 'profile') || (link.category === 'social' && link.type === 'linkedin'))),
  );

  readonly professionalCompleteness$ = this.professionalLinks$.pipe(
    map((links) => this.computeProfessionalCompleteness(links)),
  );

  /**
   * Computed property: whether professional links meet completeness requirements.
   */
  readonly hasProfessionalEligibilityContent = computed(() => {
    const links = this._userLinks.value;
    const completeness = this.computeProfessionalCompleteness(links);
    return completeness.isComplete;
  });

  constructor() {
    this.clearData();
  }

  /**
   * Clears all cached link data and resets state.
   */
  clearData(): void {
    this._userLinks.next([]);
    this._error.set(null);
    this._loadingObject.set({});
  }

  /**
   * Gets the current loading object state.
   */
  get loadingObject(): { [key: string]: boolean } {
    return this._loadingObject();
  }

  public get loadingObjectSignal(): WritableSignal<LoadingObject> {
    return this._loadingObject;
  }

  /**
   * Gets the current error state.
   */
  get error(): string | null {
    return this._error();
  }

  /**
   * Sets the loading status for a specific key.
   */
  setLoadingStatus(key: string, status: boolean): void {
    this._loadingObject.set(this.loadingService.setLoadingStatus(key, status, this._loadingObject()));
  }

  /**
   * Gets the current cached user links state.
   */
  get userLinksState(): UserLinkViewModel[] {
    return this._userLinks.value;
  }

  /**
   * Fetches all user links from the backend and caches them.
   */
  fetchUserLinks(): Observable<UserLinkViewModel[]> {
    this.setLoadingStatus('userLinks', true);
    this._error.set(null);

    return this.http.get<GetUserLinksSuccessResponse>('api/v1/user-links/filtered?categories=profile,social').pipe(
      map((response) => {
        const links = response.data.map((link) => ({
          ...link,
        } as UserLinkViewModel));
        this._userLinks.next(links);
        return links;
      }),
      catchError((error) => this.handleError(error, 'Failed to fetch user links')),
      finalize(() => this.setLoadingStatus('userLinks', false)),
    );
  }

  /**
   * Creates a new user link.
   */
  createUserLink(request: CreateUserLinkRequest): Observable<UserLinkViewModel> {
    this.setLoadingStatus('createLink', true);
    this._error.set(null);

    return this.http.post<CreateUserLinkSuccessResponse>('api/v1/user-links', request).pipe(
      map((response) => {
        const newLink = response.data as UserLinkViewModel;
        const currentLinks = this._userLinks.value;
        this._userLinks.next([...currentLinks, newLink]);
        return newLink;
      }),
      catchError((error) => this.handleError(error, 'Failed to create link')),
      finalize(() => this.setLoadingStatus('createLink', false)),
    );
  }

  /**
   * Updates an existing user link.
   */
  updateUserLink(id: number, request: UpdateUserLinkRequest): Observable<UserLinkViewModel> {
    this.setLoadingStatus('updateLink', true);
    this._error.set(null);

    return this.http.patch<UpdateUserLinkSuccessResponse>('api/v1/user-links/update/' + id, request).pipe(
      map((response) => {
        const updatedLink = response.data as UserLinkViewModel;
        const currentLinks = this._userLinks.value;
        const index = currentLinks.findIndex((link) => link.id === id);
        if (index !== -1) {
          currentLinks[index] = updatedLink;
          this._userLinks.next([...currentLinks]);
        }
        return updatedLink;
      }),
      catchError((error) => this.handleError(error, 'Failed to update link')),
      finalize(() => this.setLoadingStatus('updateLink', false)),
    );
  }

  /**
   * Deletes a user link.
   */
  deleteUserLink(id: number): Observable<void> {
    this.setLoadingStatus('deleteLink', true);
    this._error.set(null);

    return this.http.delete<DeleteUserLinkSuccessResponse>('api/v1/user-links/delete/' + id, {}).pipe(
      map((response) => {
        const deletedLinkId = response?.link_id ?? id;

        // Backend returns { message: 'link_deleted', link_id: number } on successful deletion.
        if (response?.message && response.message !== 'link_deleted') {
          throw new Error('Unexpected delete response');
        }

        const currentLinks = this._userLinks.value;
        this._userLinks.next(currentLinks.filter((link) => link.id !== deletedLinkId));
      }),
      catchError((error) => this.handleError(error, 'Failed to delete link')),
      finalize(() => this.setLoadingStatus('deleteLink', false)),
    );
  }

  /**
   * Gets all social links of a specific type.
   */
  getSocialLinksByType(type: SocialLinkType): Observable<UserLinkViewModel[]> {
    return this.userLinks$.pipe(
      map((links) => links.filter((link) => link.category === 'social' && link.type === type)),
    );
  }

  /**
   * Gets all professional links of a specific type.
   */
  getProfessionalLinksByType(type: ProfessionalLinkType): Observable<UserLinkViewModel[]> {
    return this.professionalLinks$.pipe(
      map((links) => links.filter((link) => link.type === type)),
    );
  }

  /**
   * Computes professional completeness based on current links.
   * Professional completeness requires:
   * - At least one LinkedIn link (category: social, type: linkedin)
   * - At least one of: organisation, network, or publication link
   */
  private computeProfessionalCompleteness(links: UserLinkViewModel[]): ProfessionalCompleteness {
    const hasLinkedin = links.some((link) => link.category === 'social' && link.type === 'linkedin');
    const hasOrganisation = links.some((link) => link.category === 'profile' && link.type === 'organisation');
    const hasNetwork = links.some((link) => link.category === 'profile' && link.type === 'network');
    const hasPublication = links.some((link) => link.category === 'profile' && link.type === 'publication');

    const isComplete = hasLinkedin && (hasOrganisation || hasNetwork || hasPublication);

    return {
      hasLinkedin,
      hasOrganisation,
      hasNetwork,
      hasPublication,
      isComplete,
    };
  }

  /**
   * Handles HTTP errors and converts them to user-friendly messages.
   */
  private handleError(error: unknown, fallbackMessage: string): Observable<never> {
    let errorMessage = fallbackMessage;

    if (error instanceof HttpErrorResponse) {
      const body = error.error as Partial<UserLinkErrorResponse>;
      if (body?.message) {
        errorMessage = body.message;
      } else if (error.status === 404) {
        errorMessage = 'Link not found';
      } else if (error.status === 422) {
        errorMessage = 'Invalid link data';
      } else if (error.status === 401) {
        errorMessage = 'authentication.not_authenticated';
      }
    }

    this._error.set(errorMessage);
    return throwError(() => errorMessage);
  }
}
